import React from "react";
import { motion } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import FestivityCanvas from "@/components/gravity/FestivityCanvas";
import { SITE } from "@/constants/testIds";

function scrollTo(hash) {
  const el = document.querySelector(hash);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -60, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth" });
}

export default function Hero() {
  return (
    <section
      id="home"
      className="relative min-h-[100svh] w-full overflow-hidden"
      style={{
        background:
          "radial-gradient(80% 60% at 50% 30%, #FFFFFF 0%, #FFF5E9 40%, #FFE0EC 100%)",
      }}
    >
      {/* Gravity WebGL background */}
      <div className="tb-hero-canvas-wrap">
        <FestivityCanvas ballColor="#E9298A" />
      </div>

      {/* Grain overlay */}
      <div className="absolute inset-0 grain z-[1]" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24 min-h-[100svh] flex flex-col justify-between">
        <div>
          <motion.h1
            data-testid={SITE.heroTitle}
            initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-black text-[2.6rem] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[92px] lg:leading-[0.95] mt-8 md:mt-12 text-[#1A1A1A] max-w-5xl tracking-tighter"
          >
            We build websites that{" "}
            <span className="relative inline-block">
              <span className="relative z-10">actually work</span>
              <span className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 lg:h-5 bg-[#FFE600] -z-0 -skew-y-1" />
            </span>
            .{" "}
            <span className="text-[#E9298A]">No headaches, no surprises.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-8 md:mt-10 max-w-2xl text-base md:text-lg text-[#1A1A1A]/75 leading-relaxed"
          >
            Hi, we're <span className="font-semibold text-[#1A1A1A]">Three Balls</span> — based in Hong Kong but delivering projects worldwide. From lean startup sites to complex web applications, we make fast, beautiful digital homes that help businesses grow.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid={SITE.heroCta}
              href="#projects"
              onClick={(e) => { e.preventDefault(); scrollTo("#projects"); }}
              className="tb-pill tb-pill-primary"
            >
              <Sparkles className="w-4 h-4" />
              See Our Work
              <span className="w-7 h-7 -mr-1 rounded-full bg-[#FFE600] text-[#1A1A1A] flex items-center justify-center">
                →
              </span>
            </a>
            <a
              data-testid={SITE.heroCtaSecondary}
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollTo("#contact"); }}
              className="tb-pill tb-pill-ghost"
            >
              Get a Free Quote →
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-16 flex items-center justify-between text-xs md:text-sm text-[#1A1A1A]/60 font-mono uppercase tracking-widest"
        >
          <div className="flex items-center gap-2 animate-pulse">
            <ArrowDown className="w-4 h-4" />
            <span>Scroll to explore</span>
          </div>
          <div className="hidden md:block">© 2026 — threeballs.digital</div>
        </motion.div>
      </div>
    </section>
  );
}
